import { FC, useCallback } from 'react';
import { InlineLoading, Tag } from '@carbon/react';
import { SidePanel } from '@carbon/ibm-products';
import 'components/inline-edit/ibm-products-config';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from 'components/accordion/accordion';
import { fetchProcessDetail } from './api';
import { useAgentResource } from './use-agent-resource';
import { statusTagColor } from '../format';
import type { ProcessStep } from './types';
import styles from './page.module.scss';

interface ProcessStepsSidePanelProps {
  processId: number | null;
  open: boolean;
  onClose: () => void;
}

const statusTag = (status: string) => (
  <Tag type={statusTagColor(status)} size="sm">
    {status}
  </Tag>
);

const formatTimestamp = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : new Date(iso).toLocaleString();
};

const StepCard: FC<{ step: ProcessStep; index: number }> = ({
  step,
  index,
}) => (
  <Accordion className={styles.section} defaultExpanded={index === 0}>
    <AccordionSummary>
      <div className={styles.stepSummary}>
        <span className={styles.stepName}>
          {step.order !== null ? `${step.order}. ` : ''}
          {step.name}
        </span>
        {statusTag(step.status)}
      </div>
    </AccordionSummary>
    <AccordionDetails>
      <div className={styles.sectionDetails}>
        <div className={styles.tokenGrid}>
          <span>Type</span>
          <span className={styles.stepType}>{step.type}</span>
          <span>Duration</span>
          <span>
            {step.durationSeconds !== null ? `${step.durationSeconds}s` : '—'}
          </span>
          <span>Started</span>
          <span>{formatTimestamp(step.createdAt)}</span>
          <span>Updated</span>
          <span>{formatTimestamp(step.updatedAt)}</span>
        </div>
        {step.failureReason && (
          <div className={styles.detailError}>{step.failureReason}</div>
        )}
        {step.output && (
          <Accordion className={styles.section}>
            <AccordionSummary className={styles.sectionTitle}>
              Output
            </AccordionSummary>
            <AccordionDetails>
              <div className={styles.sectionDetails}>
                <pre className={styles.codeBlock}>{step.output}</pre>
                {step.outputTruncated && (
                  <div className={styles.muted}>… (truncated)</div>
                )}
              </div>
            </AccordionDetails>
          </Accordion>
        )}
      </div>
    </AccordionDetails>
  </Accordion>
);

export const ProcessStepsSidePanel: FC<ProcessStepsSidePanelProps> = ({
  processId,
  open,
  onClose,
}) => {
  const {
    data: detail,
    loading,
    error,
  } = useAgentResource(
    useCallback(
      (controller: AbortController) =>
        fetchProcessDetail(processId as number, controller),
      [processId]
    ),
    [processId],
    open && processId !== null
  );

  return (
    <SidePanel
      open={open}
      onRequestClose={onClose}
      title={`Workflow Process #${processId ?? ''}`}
      subtitle={detail ? `${detail.name} · ${detail.status}` : undefined}
      placement="right"
      size="lg"
      slideIn
      selectorPageContent="#main-content"
    >
      <div className={styles.detailBody}>
        {loading && <InlineLoading description="Loading workflow..." />}
        {error && <div className={styles.detailError}>{error}</div>}

        {detail && (
          <>
            <div className={styles.summaryRow}>
              {statusTag(detail.status)}
              <span className={styles.summaryMeta}>
                {detail.completedSteps}/{detail.totalSteps} steps
              </span>
              {detail.failedSteps > 0 && (
                <span className={styles.summaryMeta}>
                  {detail.failedSteps} failed
                </span>
              )}
            </div>

            <div className={styles.tokenGrid}>
              <span>Type</span>
              <span className={styles.stepType}>{detail.type}</span>
              <span>Overall status</span>
              <span>{detail.overallStatus}</span>
              {detail.itemType && (
                <>
                  <span>Item</span>
                  <span>
                    {detail.itemType} #{detail.itemId}
                  </span>
                </>
              )}
              <span>Created</span>
              <span>{formatTimestamp(detail.createdAt)}</span>
              <span>Updated</span>
              <span>{formatTimestamp(detail.updatedAt)}</span>
            </div>

            {detail.failureReason && (
              <div className={styles.detailError}>{detail.failureReason}</div>
            )}

            <div className={styles.sectionTitle}>
              Steps ({detail.totalSteps})
            </div>
            {detail.steps.map((step, idx) => (
              <StepCard key={step.id} step={step} index={idx} />
            ))}
          </>
        )}
      </div>
    </SidePanel>
  );
};
